import api from './api';

/**
 * Live-Blog (public) — KI-Live-Ticker zu DartConnect-Turnieren.
 * Wird auf der News-Detailseite als dritter Reiter angezeigt, wenn ein
 * Artikel mit einem Live-Blog verknüpft ist (article.liveBlogId).
 */

export interface LiveBlog {
  // WICHTIG: ALLES vom Backend muss nullable sein für maximale Robustheit!
  _id?: string;
  id?: string;
  title?: string | null;
  tournamentId?: string | null;
  status?: string | null; // 'live' | 'finished'
  entryCount?: number | null;
  lastUpdateAt?: number | null;
  lastCheckedAt?: number | null;
  lastError?: string | null;
  createdAt?: number | null;
}

export interface LiveBlogEntry {
  _id?: string;
  id?: string;
  liveBlogId?: string | null;
  title?: string | null;
  content?: string | null;
  contentHtml?: string | null;
  aiGenerated?: boolean | null;
  createdAt?: number | null;
}

export interface LiveBlogResponse {
  liveBlog: LiveBlog;
  entries: LiveBlogEntry[];
}

/** Laufender Blog + verknüpfter Artikel (Startseiten-Leiste) */
export interface ActiveLiveBlog extends LiveBlog {
  article?: {
    slug?: string | null;
    title?: string | null;
    image?: string | null;
  } | null;
}

class LiveBlogService {
  /** Alle laufenden Blogs samt verknüpftem Artikel (für die Startseite) */
  async getActiveLiveBlogs(): Promise<ActiveLiveBlog[]> {
    try {
      const response = await api.get('/api/live-blogs/active');
      return Array.isArray(response.data?.liveBlogs) ? response.data.liveBlogs : [];
    } catch (error) {
      console.error('Fehler beim Laden der aktiven Live-Blogs:', error);
      return [];
    }
  }

  /** Artikel-Thumbnail eines aktiven Blogs (relative Pfade → API-URL) */
  getArticleImageUrl(blog?: ActiveLiveBlog): string | null {
    const image = blog?.article?.image;
    if (!image || typeof image !== 'string' || !image.trim()) return null;
    const path = image.trim();
    if (path.startsWith('http')) return path;
    return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  async getLiveBlog(id: string): Promise<LiveBlogResponse | null> {
    try {
      const response = await api.get(`/api/live-blog/${id}`);
      if (!response.data?.liveBlog) return null;
      return {
        liveBlog: response.data.liveBlog,
        entries: Array.isArray(response.data.entries) ? response.data.entries : [],
      };
    } catch (error) {
      console.error('Fehler beim Laden des Live-Blogs:', error);
      return null;
    }
  }

  /** Uhrzeit eines Eintrags, z.B. "19:45" */
  formatEntryTime(entry?: LiveBlogEntry): string {
    if (!entry?.createdAt) return '';
    try {
      return new Date(entry.createdAt).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }

  /** Datum + Uhrzeit, z.B. "24.08.2026, 19:45" */
  formatEntryDateTime(entry?: LiveBlogEntry): string {
    if (!entry?.createdAt) return '';
    try {
      return new Date(entry.createdAt).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }
}

const liveBlogService = new LiveBlogService();
export default liveBlogService;
